"use client";

import { useEffect, useSyncExternalStore } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

import {
  getPinnedChatLayout,
  setPinnedChatLayout,
  subscribePinnedChatLayout,
  type PinnedChatLayoutView,
} from "@/lib/chat/chat-layout-persistence";
import { isMobileViewport } from "@/hooks/use-mobile";

function getPinnedSnapshot(): PinnedChatLayoutView | null {
  return getPinnedChatLayout();
}

function getPinnedServerSnapshot(): null {
  return null;
}

/** Avoid duplicate router.replace when multiple sync cycles target the same href. */
let lastReplaceHref: string | null = null;

function isOpenChatUrlView(
  value: string | null,
): value is PinnedChatLayoutView {
  return value === "split-view" || value === "chat";
}

function coercePinnedViewForViewport(
  view: PinnedChatLayoutView,
): PinnedChatLayoutView {
  if (view === "split-view" && isMobileViewport()) {
    return "chat";
  }
  return view;
}

/**
 * Single mount-point sync for pinned chat layout ↔ URL `view` param.
 * Must not live in useChatSwitch — that hook is used in many components and
 * duplicated effects caused navigation thrashing / apparent hangs.
 *
 * Authority rules (avoid chat ↔ split-view oscillation):
 * - When URL already has `chat` | `split-view`, URL wins → pin follows URL only.
 * - When URL omits an open chat view, pin is restored onto the URL (browse persistence).
 * - Never overwrite a live `chat`/`split-view` URL with a stale React pin snapshot.
 * - On mobile, never persist or restore `split-view` (always `chat`).
 */
export function ChatLayoutUrlSync(): null {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const pinnedLayout = useSyncExternalStore(
    subscribePinnedChatLayout,
    getPinnedSnapshot,
    getPinnedServerSnapshot,
  );

  const urlView = searchParams.get("view");

  // Normalize a desktop split pin as soon as we are on a mobile viewport.
  useEffect(() => {
    if (pinnedLayout !== "split-view" || !isMobileViewport()) {
      return;
    }
    setPinnedChatLayout("chat");
  }, [pinnedLayout]);

  // Mobile: coerce split-view → chat so URL/pin never keep a desktop split on small screens.
  useEffect(() => {
    if (urlView !== "split-view" || !isMobileViewport()) {
      return;
    }
    const params = new URLSearchParams(searchParams.toString());
    params.set("view", "chat");
    const href = `${pathname}?${params.toString()}`;
    if (lastReplaceHref === href) {
      return;
    }
    lastReplaceHref = href;
    setPinnedChatLayout("chat");
    router.replace(href);
  }, [pathname, router, searchParams, urlView]);

  // URL → pin (URL is source of truth while an open chat layout is on the query string).
  useEffect(() => {
    if (!isOpenChatUrlView(urlView)) {
      return;
    }
    const pinView = coercePinnedViewForViewport(urlView);
    if (getPinnedChatLayout() === pinView) {
      return;
    }
    setPinnedChatLayout(pinView);
  }, [urlView]);

  // Pin → URL only when the URL is missing an open chat layout (e.g. in-app nav omitted `view`).
  useEffect(() => {
    if (!pinnedLayout) {
      lastReplaceHref = null;
      return;
    }

    const currentUrlView = searchParams.get("view");

    // Live chat/split URL must not be overwritten by a stale pin from a prior render.
    if (isOpenChatUrlView(currentUrlView)) {
      return;
    }

    if (currentUrlView === "app") {
      return;
    }

    const viewToWrite = coercePinnedViewForViewport(pinnedLayout);
    if (viewToWrite !== pinnedLayout) {
      setPinnedChatLayout(viewToWrite);
    }

    const params = new URLSearchParams(searchParams.toString());
    params.set("view", viewToWrite);
    const href = `${pathname}?${params.toString()}`;
    if (lastReplaceHref === href) {
      return;
    }
    lastReplaceHref = href;
    router.replace(href);
  }, [pathname, pinnedLayout, router, searchParams]);

  return null;
}
